(function () {
  'use strict';

  const resolvedQueries = new Map();
  const pendingQueries = new Map();
  const cacheLimit = 40;
  const alternativeLimit = 8;
  const expandedWordLimit = 8;
  const fallbackRequestLimit = 2;
  const wordMetadata = new Map();
  const compiledQueries = new Map();
  let compiledDictionary = null;

  function normalizeQuery(value) {
    return String(value || '').normalize('NFC').trim().replace(/\s+/g, ' ');
  }

  function skuSuffix(value) {
    return normalizeQuery(value).match(/^\*-([0-9]{2})$/)?.[1] || '';
  }

  function parseInput(value) {
    const rawQuery = normalizeQuery(value == null ? '' : String(value));
    if (!rawQuery.startsWith('%')) {
      return { query: rawQuery, unavailableMode: 'hide', stockMode: 'available' };
    }

    const query = rawQuery.replace(/^%+/, '').replace(/%+$/, '').trim();
    const soldOutOnly = !!query && rawQuery.endsWith('%');
    return {
      query,
      unavailableMode: soldOutOnly ? 'show' : 'last',
      stockMode: soldOutOnly ? 'soldout' : 'all',
    };
  }

  function applyStockParams(params, config) {
    params.set('options[unavailable_products]', config.stockMode === 'soldout' ? 'show' : config.unavailableMode);
    // A new stock shortcut replaces every old availability choice, including
    // duplicate values serialized from desktop and mobile filter forms.
    params.delete('filter.v.availability');
    if (config.stockMode === 'soldout') params.set('filter.v.availability', '0');
    return params;
  }

  function stockConfigFromParams(params) {
    const availabilityValues = params.getAll('filter.v.availability')
      .flatMap((value) => value.split(','))
      .map((value) => value.trim())
      .filter((value) => value === '0' || value === '1');
    const mode = params.get('options[unavailable_products]');
    const hasAvailable = availabilityValues.includes('1');
    const hasUnavailable = availabilityValues.includes('0');
    // Explicit checkbox choices take precedence over a stale hidden mode.
    if (hasAvailable && hasUnavailable) {
      return { unavailableMode: mode === 'last' ? 'last' : 'show', stockMode: 'all' };
    }
    if (hasUnavailable) {
      return { unavailableMode: 'show', stockMode: 'soldout' };
    }
    if (hasAvailable) {
      return { unavailableMode: 'hide', stockMode: 'available' };
    }
    if (mode === 'last' || mode === 'show') {
      return { unavailableMode: mode, stockMode: 'all' };
    }
    return { unavailableMode: 'hide', stockMode: 'available' };
  }

  // Leave explicit phrases, fields, exclusions and wildcards intact.
  function isPlainQuery(value) {
    const query = normalizeQuery(value);
    return !!query && !/["\\():*]|(?:^|\s)(?:AND|OR|NOT)(?:\s|$)|(?:^|\s)-\S/.test(query);
  }

  function lastWordPrefix(query) {
    if (!isPlainQuery(query)) return '';
    const lastWord = query.split(' ').pop();
    // Numeric searches and SKU-like tokens stay exact; only words can expand.
    return /^(?:\p{L}\p{M}*){3,}$/u.test(lastWord) ? lastWord : '';
  }

  function alphabeticWord(value) {
    return /^(?:\p{L}\p{M}*)+$/u.test(value);
  }

  function wordKey(value) {
    return value.normalize('NFC').toLowerCase();
  }

  function canRelax(word) {
    const letters = Array.from(wordKey(word));
    return letters.length >= 5 && letters.length <= 48 && alphabeticWord(word) &&
      letters.every((letter) => letter.toLowerCase() !== letter.toUpperCase());
  }

  function metadataFor(word) {
    if (wordMetadata.has(word)) return wordMetadata.get(word);
    const letters = Array.from(word);
    const counts = new Map();
    for (const letter of letters) counts.set(letter, (counts.get(letter) || 0) + 1);
    const info = { letters, counts, relaxable: canRelax(word) };
    if (wordMetadata.size >= 55000) wordMetadata.clear();
    wordMetadata.set(word, info);
    return info;
  }

  // Damerau-Levenshtein distance: insertion, deletion, substitution and a
  // transposition all cost one, including a mistake near the start of a word.
  function spellingDistance(left, right, limit) {
    const leftInfo = metadataFor(left), rightInfo = metadataFor(right);
    const a = leftInfo.letters;
    const b = rightInfo.letters;
    if (Math.abs(a.length - b.length) > limit) return limit + 1;
    let missing = 0, extra = 0;
    for (const [letter, count] of leftInfo.counts) missing += Math.max(0, count - (rightInfo.counts.get(letter) || 0));
    for (const [letter, count] of rightInfo.counts) extra += Math.max(0, count - (leftInfo.counts.get(letter) || 0));
    if (Math.max(missing, extra) > limit) return limit + 1;
    const infinity = a.length + b.length;
    const distance = Array.from({ length: a.length + 2 }, () => new Array(b.length + 2).fill(infinity));
    for (let i = 0; i <= a.length; i++) distance[i + 1][1] = i;
    for (let j = 0; j <= b.length; j++) distance[1][j + 1] = j;
    const last = new Map();
    for (let i = 1; i <= a.length; i++) {
      let matched = 0;
      for (let j = 1; j <= b.length; j++) {
        const previousRow = last.get(b[j - 1]) || 0;
        const previousColumn = matched;
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        if (!cost) matched = j;
        distance[i + 1][j + 1] = Math.min(
          distance[i][j] + cost, distance[i + 1][j] + 1, distance[i][j + 1] + 1,
          distance[previousRow][previousColumn] + i - previousRow - 1 + 1 + j - previousColumn - 1
        );
      }
      last.set(a[i - 1], i);
    }
    return distance[a.length + 1][b.length + 1];
  }

  function candidateWords(texts) {
    const words = [];
    const seen = new Set();
    for (const text of texts) {
      for (const candidate of String(text || '').match(/[\p{L}\p{M}\p{N}]+/gu) || []) {
        const key = wordKey(candidate);
        if (!alphabeticWord(key) || seen.has(key)) continue;
        seen.add(key);
        words.push(key);
        if (words.length === 2048) return words;
      }
    }
    return words;
  }

  function alternativesFor(word, candidates, lastWord, knownWords = null) {
    const key = wordKey(word);
    const letters = Array.from(key);
    if (!alphabeticWord(key) || letters.length < (lastWord ? 3 : 5)) return [word];
    const stem = canRelax(word) ? letters.slice(0, -1).join('') : '';
    const groups = [[], []];
    for (const candidate of candidates) {
      if (candidate === key) continue;
      if (lastWord && candidate.startsWith(key)) groups[0].push(candidate);
      else if (stem && candidate.startsWith(stem) && canRelax(candidate)) groups[1].push(candidate);
    }
    const related = groups.flat();
    // A real catalog word keeps its meaning. For example, guralica must not
    // become duvalica merely because the two words are two edits apart.
    if (related.length || !stem || knownWords?.has(key)) {
      return [word, ...related].slice(0, alternativeLimit);
    }
    const limit = letters.length >= 8 ? 2 : 1;
    const matches = [];
    for (const [order, candidate] of candidates.entries()) {
      if (candidate === key) continue;
      // All query tokens are NFC. Check the cheap length bound before Unicode
      // validation and reuse character counts across successive keystrokes.
      if (!/[\uD800-\uDFFF]/.test(candidate) && Math.abs(candidate.length - letters.length) > limit) continue;
      const info = metadataFor(candidate);
      if (!info.relaxable) continue;
      const length = info.letters.length;
      if (Math.abs(length - letters.length) > limit) continue;
      const distance = spellingDistance(key, candidate, limit);
      if (distance > limit || distance / Math.max(length, letters.length) > 0.25) continue;
      let prefix = 0;
      while (key[prefix] && key[prefix] === candidate[prefix]) prefix++;
      matches.push({ candidate, distance, difference: Math.abs(length - letters.length), prefix, order });
    }
    // Prefer the strongest catalog evidence among equally close spellings.
    // Adding every tied word can turn projektroi into unrelated projektili.
    matches.sort((a, b) => a.distance - b.distance || a.order - b.order || a.difference - b.difference || b.prefix - a.prefix);
    const best = matches.slice(0, 1).map(match => match.candidate);
    const corrections = [...best];
    // Include actual word forms of the correction (projektori → projektor),
    // so a typo in a plural can still find products named in the singular.
    for (const correction of best) {
      const correctedStem = /[aeiouаеёиоуыэюя]$/u.test(correction)
        ? Array.from(correction).slice(0, -1).join('') : correction;
      if (correctedStem.length < 5) continue;
      for (const candidate of candidates) {
        if (candidate.startsWith(correctedStem) && canRelax(candidate)) corrections.push(candidate);
      }
    }
    return [word, ...new Set(corrections)].slice(0, alternativeLimit);
  }

  function catalogWords() {
    const words = window.WokieeSearchLexicon?.peek();
    return Array.isArray(words) ? words : null;
  }

  function lexicalClause(word) {
    const quoted = `"${word}"`;
    // Shopify can match a hyphen-delimited SKU segment through its explicit
    // field even when the same quoted term returns no product. Keep the text
    // alternative and accept only safe code characters in the field payload.
    const skuCode = /[0-9]/.test(word) && /^[\p{L}\p{M}\p{N}._/-]+$/u.test(word) && !word.startsWith('-');
    return skuCode ? `(${quoted} OR variants.sku:${word})` : quoted;
  }

  // Predictive products supply real words, never the final product list. We
  // retain lexical OR clauses instead of sending an unbounded semantic query.
  function fullQuery(value, texts = []) {
    const query = normalizeQuery(value);
    if (!isPlainQuery(query)) return query;

    const words = query.split(' ');
    const dictionary = catalogWords();
    if (dictionary !== compiledDictionary) { compiledQueries.clear(); compiledDictionary = dictionary; }
    const seedWords = candidateWords(texts);
    const compiledKey = JSON.stringify([query, seedWords]);
    if (compiledQueries.has(compiledKey)) return compiledQueries.get(compiledKey);
    const knownWords = dictionary ? new Set(dictionary) : null;
    // The predictive text budget must not truncate the complete catalog index.
    const candidates = [...new Set([...seedWords, ...(dictionary || [])])];
    const compiled = words.map((word, index) => {
      if (index >= expandedWordLimit) return lexicalClause(word);
      const lastWord = index === words.length - 1;
      const alternatives = alternativesFor(word, candidates, lastWord, knownWords);
      if (alternatives.length === 1) return lexicalClause(word);
      const oldPrefixGroup = lastWord && alternatives.every((candidate) => wordKey(candidate).startsWith(wordKey(word)));
      const clauses = alternatives.map((candidate) => `"${candidate}"`);
      // The extra parentheses identify our broadened group to the Liquid
      // display decoder, which restores the original word in every AND slot.
      if (!oldPrefixGroup) clauses[0] = `(${clauses[0]})`;
      return `(${clauses.join(' OR ')})`;
    }).join(' AND ');
    compiledQueries.set(compiledKey, compiled);
    if (compiledQueries.size > 80) compiledQueries.delete(compiledQueries.keys().next().value);
    return compiled;
  }

  function completionTexts(data, includeQueries = true) {
    const results = data?.resources?.results || {};
    const products = Array.isArray(results.products) ? results.products.slice(0, 10) : [];
    const queries = Array.isArray(results.queries) ? results.queries.slice(0, 10) : [];
    // Prefer visible names before descriptions so a verbose first description
    // cannot consume every alternative before the other product titles.
    const texts = includeQueries ? queries.map((query) => query.text) : [];
    products.forEach((product) => {
      texts.push(product.title);
      if (Array.isArray(product.variants)) {
        product.variants.forEach((variant) => texts.push(variant.title));
      }
    });
    products.forEach((product) => {
      texts.push(product.vendor, product.type, product.product_type);
      if (Array.isArray(product.tags)) texts.push(...product.tags);
      else if (typeof product.tags === 'string') texts.push(product.tags);
    });
    products.forEach((product) => {
      texts.push(String(product.body || '')
        .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
        .replace(/<[^>]*>/g, ' '));
    });
    return texts;
  }

  function predictiveUrl(query, unavailableMode) {
    const root = window.Shopify?.routes?.root || '/';
    const route = window.routes?.predictive_search_url || `${root}search/suggest`;
    const url = new URL(route, window.location.origin);
    url.pathname = url.pathname.replace(/\/$/, '');
    if (!url.pathname.endsWith('.json')) url.pathname += '.json';
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[limit]', '10');
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[options][unavailable_products]', unavailableMode);
    return url.toString();
  }

  async function resolveQuery(value, unavailableMode, seedTexts = [], options = {}) {
    const query = normalizeQuery(value);
    const words = query.split(' ');
    const editableWords = isPlainQuery(query) ? words.slice(0, expandedWordLimit)
      .filter(canRelax) : [];
    const prefix = lastWordPrefix(query);
    const hasExactEvidence = (candidates) => editableWords.length > 0 &&
      editableWords.every(word => candidates.includes(wordKey(word))) &&
      (!prefix || canRelax(prefix) || candidates.some(candidate => candidate.startsWith(wordKey(prefix))));
    // Use current product suggestions alongside the ready, word-only index.
    if (hasExactEvidence(candidateWords(seedTexts))) return fullQuery(query, seedTexts);
    const strictQuery = fullQuery(query, seedTexts);
    if (!lastWordPrefix(query) && !editableWords.length) return strictQuery;

    const readyWords = catalogWords();
    if (readyWords) {
      const known = new Set(readyWords);
      const clauses = strictQuery.split(' AND ');
      const covered = words.slice(0, expandedWordLimit).every((word, index) => {
        const expandable = canRelax(word) || (index === words.length - 1 && !!prefix);
        return !expandable || known.has(wordKey(word)) || clauses[index]?.includes(' OR ');
      });
      if (covered) return strictQuery;
    }

    const mode = ['hide', 'last', 'show'].includes(unavailableMode) ? unavailableMode : 'hide';
    const currentCacheKey = () => `${window.WokieeSearchLexicon?.revision() || 0}\u0000${mode}\u0000${query}`;
    const cacheKey = currentCacheKey();
    if (resolvedQueries.has(cacheKey)) return resolvedQueries.get(cacheKey);
    // Enter never waits for network discovery. Optional correction suggestions
    // can finish separately; the literal term always reaches Shopify search.
    if (options.waitForSuggestions === false) return strictQuery;
    if (pendingQueries.has(cacheKey)) return pendingQueries.get(cacheKey);

    const request = (async () => {
      const controller = new AbortController();
      let timeout;
      const texts = [];
      const productTexts = [];
      let complete = true;
      let confirmed = false;
      try {
        const deadline = new Promise((resolve, reject) => {
          timeout = window.setTimeout(() => {
            controller.abort();
            reject(new Error('Search completion timed out.'));
          }, 2000);
        });
        const fetchWords = async (term) => {
          const response = await fetch(predictiveUrl(term, mode), {
            signal: controller.signal,
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Search completion failed (${response.status}).`);
          const data = await response.json();
          if (!controller.signal.aborted) {
            texts.push(...completionTexts(data));
            productTexts.push(...completionTexts(data, false));
          }
        };
        const expand = async () => {
          await fetchWords(query);
          if (controller.signal.aborted) return;
          const candidates = candidateWords(texts);
          confirmed = hasExactEvidence(candidateWords(productTexts));
          if (confirmed) return;
          // Only words with no useful alternative need a shorter lookup. At
          // most two extra requests share the original two-second deadline.
          const probes = [...new Set(editableWords.filter((word) =>
            alternativesFor(word, candidates, word === words[words.length - 1]).length === 1
          ).reverse().map((word) => Array.from(wordKey(word)).slice(0, -1).join('')))]
            .slice(0, fallbackRequestLimit);
          await Promise.all(probes.map((probe) => fetchWords(probe).catch(() => { complete = false; })));
        };
        await Promise.race([expand(), deadline]);
      } catch (error) {
        complete = false;
      } finally {
        window.clearTimeout(timeout);
      }
      // Keep verified words even if one data source failed. No semantic fallback.
      const resolvedQuery = fullQuery(query, texts);
      if (complete) {
        resolvedQueries.set(currentCacheKey(), resolvedQuery);
        if (resolvedQueries.size > cacheLimit) resolvedQueries.delete(resolvedQueries.keys().next().value);
      }
      return resolvedQuery;
    })();
    pendingQueries.set(cacheKey, request);
    void request.finally(() => pendingQueries.delete(cacheKey));
    return request;
  }

  async function resolveSearch(value, unavailableMode) {
    const original = normalizeQuery(value);
    const query = await resolveQuery(original, unavailableMode);
    let correction = '';
    if (isPlainQuery(original)) {
      const words = original.split(' ');
      const clauses = query.split(' AND ');
      if (clauses.length === words.length) {
        const phrase = clauses.map((clause, index) => {
          const alternative = /^\(\(?"[^"]+"\)? OR "([^"]+)"/.exec(clause);
          return alternative ? alternative[1] : words[index];
        }).join(' ');
        if (wordKey(phrase) !== wordKey(original)) correction = phrase;
      }
    }
    return { query, correction };
  }

  window.WokieeSearchQuery = Object.freeze({
    fullQuery, resolveQuery, resolveSearch, isPlainQuery, parseInput, applyStockParams, stockConfigFromParams, skuSuffix,
  });
})();
